import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "form-group" }
const _hoisted_2 = {
  for: "taskName",
  class: "required"
}
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = {
  key: 0,
  class: "error-message"
}
const _hoisted_5 = { class: "form-group" }
const _hoisted_6 = { for: "taskDescription" }
const _hoisted_7 = ["placeholder"]
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = { for: "taskAmount" }
const _hoisted_10 = { class: "currency-input" }
const _hoisted_11 = { class: "currency-symbol" }
const _hoisted_12 = ["placeholder"]
const _hoisted_13 = {
  key: 0,
  class: "error-message"
}
const _hoisted_14 = {
  key: 0,
  class: "form-group"
}
const _hoisted_15 = { for: "taskPhase" }
const _hoisted_16 = { value: null }
const _hoisted_17 = ["value"]
const _hoisted_18 = { class: "form-group" }
const _hoisted_19 = { for: "taskIntervenant" }
const _hoisted_20 = { value: null }
const _hoisted_21 = ["value"]
const _hoisted_22 = { class: "form-row" }
const _hoisted_23 = { class: "form-group" }
const _hoisted_24 = { for: "taskStartDate" }
const _hoisted_25 = { class: "form-group" }
const _hoisted_26 = { for: "taskEndDate" }
const _hoisted_27 = ["min"]
const _hoisted_28 = { class: "form-actions" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = {
  key: 0,
  class: "spinner"
}

import { ref as vueRef, computed as vueComputed, watch as vueWatch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import { getUserCurrency } from '@/utils/currencyUtils'
import Modal from '@/components/base/Modal.vue'
import projectTasksService from '@/services/projectTasks.service'

// Aliases to avoid TypeScript errors
interface Task {
  id?: number
  projectId: number
  name: string
  description?: string
  amount: number
  intervenantName?: string
  intervenantId?: number | null
  phaseId?: number | null
  phaseName?: string
  startDate?: string
  endDate?: string
}

interface Project {
  id: number
  multiPhase?: boolean
}

interface Phase {
  id: number
  name: string
}

interface Intervenant {
  id: number
  name: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TaskFormModal',
  props: {
    show: { type: Boolean },
    task: {},
    projectId: {},
    project: {},
    phases: {},
    intervenants: {}
  },
  emits: ["close", "saved"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const ref = vueRef
const computed = vueComputed
const watch = vueWatch

const props = __props

const emit = __emit

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// Form state
const form = ref<Task>({
  projectId: props.projectId,
  name: '',
  description: '',
  amount: 0,
  intervenantId: null,
  phaseId: null,
  startDate: '',
  endDate: ''
})

const errors = ref<Record<string, string>>({})
const submitting = ref(false)

// Computed
const isEditing = computed(() => !!props.task?.id)
const userCurrency = computed(() => getUserCurrency())

// Methods
const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.name || form.value.name.trim().length < 3) {
    errors.value.name = t('workspace.modal.errors.nameRequired')
    return false
  }
  
  if (form.value.amount < 0) {
    errors.value.amount = t('workspace.modal.errors.invalidAmount')
    return false
  }
  
  if (form.value.startDate && form.value.endDate) {
    if (new Date(form.value.endDate) < new Date(form.value.startDate)) {
      errors.value.endDate = t('workspace.modal.errors.endDateBeforeStart')
      return false
    }
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return
  
  submitting.value = true
  
  try {
    const taskData = {
      ...form.value,
      projectId: props.projectId
    }
    
    if (isEditing.value && form.value.id) {
      // Update existing task
      await projectTasksService.updateTask(
        props.projectId.toString(),
        form.value.id.toString(),
        taskData
      )
      notifySuccess(t('workspace.success.taskUpdated'))
    } else {
      // Create new task
      await projectTasksService.createTask(
        props.projectId.toString(),
        taskData
      )
      notifySuccess(t('workspace.success.taskCreated'))
    }
    
    emit('saved')
    closeModal()
  } catch (error) {
    notifyError(t('workspace.error.taskSaveFailed'))
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  form.value = {
    projectId: props.projectId,
    name: '',
    description: '',
    amount: 0,
    intervenantId: null,
    phaseId: null,
    startDate: '',
    endDate: ''
  }
  errors.value = {}
  emit('close')
}

// Watch for task changes (editing mode)
watch(() => props.task, (newTask: Task | null) => {
  if (newTask) {
    form.value = {
      ...newTask,
      projectId: props.projectId
    }
  } else {
    form.value = {
      projectId: props.projectId,
      name: '',
      description: '',
      amount: 0,
      intervenantId: null,
      phaseId: null,
      startDate: '',
      endDate: ''
    }
  }
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: _ctx.show,
    title: _unref(isEditing) ? _ctx.$t('workspace.modal.editTask') : _ctx.$t('workspace.modal.createTask'),
    size: "large",
    "close-on-backdrop-click": !_unref(submitting),
    loading: _unref(submitting),
    "no-padding": "",
    onClose: closeModal
  }, {
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "task-form"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("label", _hoisted_2, _toDisplayString(_ctx.$t('workspace.modal.taskName')), 1),
          _withDirectives(_createElementVNode("input", {
            id: "taskName",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).name) = $event)),
            type: "text",
            placeholder: _ctx.$t('workspace.modal.taskNamePlaceholder'),
            required: "",
            minlength: "3",
            maxlength: "100"
          }, null, 8, _hoisted_3), [
            [_vModelText, _unref(form).name]
          ]),
          (_unref(errors).name)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(errors).name), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('workspace.modal.description')), 1),
          _withDirectives(_createElementVNode("textarea", {
            id: "taskDescription",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).description) = $event)),
            placeholder: _ctx.$t('workspace.modal.descriptionPlaceholder'),
            maxlength: "500",
            rows: "3"
          }, null, 8, _hoisted_7), [
            [_vModelText, _unref(form).description]
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("label", _hoisted_9, _toDisplayString(_ctx.$t('workspace.modal.amount')), 1),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(userCurrency)), 1),
            _withDirectives(_createElementVNode("input", {
              id: "taskAmount",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).amount) = $event)),
              type: "number",
              min: "0",
              step: "0.01",
              placeholder: _ctx.$t('workspace.modal.amountPlaceholder')
            }, null, 8, _hoisted_12), [
              [
                _vModelText,
                _unref(form).amount,
                void 0,
                { number: true }
              ]
            ])
          ]),
          (_unref(errors).amount)
            ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_unref(errors).amount), 1))
            : _createCommentVNode("", true)
        ]),
        (_ctx.project?.multiPhase)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("label", _hoisted_15, _toDisplayString(_ctx.$t('workspace.modal.phase')), 1),
              _withDirectives(_createElementVNode("select", {
                id: "taskPhase",
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).phaseId) = $event))
              }, [
                _createElementVNode("option", _hoisted_16, _toDisplayString(_ctx.$t('workspace.modal.selectPhase')), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.phases, (phase) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: phase.id,
                    value: phase.id
                  }, _toDisplayString(phase.name), 9, _hoisted_17))
                }), 128))
              ], 512), [
                [_vModelSelect, _unref(form).phaseId]
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("label", _hoisted_19, _toDisplayString(_ctx.$t('workspace.modal.intervenant')), 1),
          _withDirectives(_createElementVNode("select", {
            id: "taskIntervenant",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(form).intervenantId) = $event))
          }, [
            _createElementVNode("option", _hoisted_20, _toDisplayString(_ctx.$t('workspace.modal.selectIntervenant')), 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.intervenants, (intervenant) => {
              return (_openBlock(), _createElementBlock("option", {
                key: intervenant.id,
                value: intervenant.id
              }, _toDisplayString(intervenant.name), 9, _hoisted_21))
            }), 128))
          ], 512), [
            [_vModelSelect, _unref(form).intervenantId]
          ])
        ]),
        _createElementVNode("div", _hoisted_22, [
          _createElementVNode("div", _hoisted_23, [
            _createElementVNode("label", _hoisted_24, _toDisplayString(_ctx.$t('workspace.modal.startDate')), 1),
            _withDirectives(_createElementVNode("input", {
              id: "taskStartDate",
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(form).startDate) = $event)),
              type: "date"
            }, null, 512), [
              [_vModelText, _unref(form).startDate]
            ])
          ]),
          _createElementVNode("div", _hoisted_25, [
            _createElementVNode("label", _hoisted_26, _toDisplayString(_ctx.$t('workspace.modal.endDate')), 1),
            _withDirectives(_createElementVNode("input", {
              id: "taskEndDate",
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(form).endDate) = $event)),
              type: "date",
              min: _unref(form).startDate
            }, null, 8, _hoisted_27), [
              [_vModelText, _unref(form).endDate]
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-secondary",
            onClick: closeModal
          }, _toDisplayString(_ctx.$t('common.cancel')), 1),
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-primary",
            disabled: _unref(submitting)
          }, [
            (_unref(submitting))
              ? (_openBlock(), _createElementBlock("span", _hoisted_30))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_unref(submitting) ? _ctx.$t('common.saving') : _ctx.$t('common.save')), 1)
          ], 8, _hoisted_29)
        ])
      ], 32)
    ]),
    _: 1
  }, 8, ["show", "title", "close-on-backdrop-click", "loading"]))
}
}

})